import { View } from './View.js'
import { Layout } from './Layout'
import { Nav } from './Nav.js'
import { Transition } from './Transition.js'
import { Events, EventType } from '../utils/Events.js'
import { processUrl } from '../utils/Url.js'
import { parseDom } from '../utils/Dom.js'

export class Router {
  static init({ views = { default: View }, transitions = { default: Transition } }) {
    this.views = views
    this.transitions = transitions

    this.defaultView = this.views.default
    this.defaultTransition = this.transitions.default

    this.cache = new Map()
    this.isTransitioning = false
    this.currentCacheEntry = null
    this.wrapper = document.querySelector('main')
    this.removeOldContent = true
    this.allowInterruption = false
    this.bypassCache = false
    this.isPopping = false
    this.active = true

    this.attachEvents('a:not([target]):not([href^=\\#]):not([data-router-ignore])')

    this.currentLocation = processUrl(window.location.href)

    this.cache.set(this.currentLocation.href, this.createCacheEntry(document.cloneNode(true)))

    this.currentCacheEntry = this.cache.get(this.currentLocation.href)
    this.currentCacheEntry.view.init()
    this.currentCacheEntry.view.addEvents()
  }

  static destroy() {
    Events.off('click', 'a:not([target]):not([href^=\\#]):not([data-router-ignore])', this.onClick)
    Events.off('popstate', window, this.onPopstate)
  }

  static setDefaultView(view) {
    this.defaultView = this.views[view]
  }

  static setDefaultTransition(transition) {
    this.defaultTransition = this.transitions[transition]
  }

  // static addRoute(fromPattern, toPattern, transition) {
  // 	if (!this.router) {
  // 		this.router = new RouteStore()
  // 	}

  // 	this.router.add(fromPattern, toPattern, transition)
  // }

  static preload(url, preloadAssets = false) {
    // convert relative URLs to absolute
    url = processUrl(url).href

    if (!this.cache.has(url)) {
      return this.fetch(url, false).then(async (newPage) => {
        this.cache.set(url, this.createCacheEntry(newPage))

        if (preloadAssets) {
          this.cache.get(url).view.createDom()
        }
      })
    }

    return Promise.resolve()
  }

  static updateCache(url) {
    const key = processUrl(url || window.location.href).href

    if (this.cache.has(key)) {
      this.cache.delete(key)
    }

    this.cache.set(key, this.createCacheEntry(document.cloneNode(true)))
  }

  static clearCache(url) {
    const key = processUrl(url || window.location.href).href

    if (this.cache.has(key)) {
      this.cache.delete(key)
    }
  }

  static navigateTo(url, transition = false, trigger = false) {
    return new Promise((resolve, reject) => {
      // Don't allow multiple navigations to occur at once
      if (!this.allowInterruption && this.isTransitioning) {
        reject()
        return
      }

      this.isTransitioning = true
      this.isPopping = true
      this.targetLocation = processUrl(url)
      this.popTarget = window.location.href

      const TransitionClass = new (this.chooseTransition(transition))({ wrapper: this.wrapper })

      let navigationPromise

      if (this.bypassCache || !this.cache.has(this.targetLocation.href) || this.cache.get(this.targetLocation.href).skipCache) {
        const fetched = this.fetch(this.targetLocation.raw).then((newPage) => {
          this.cache.set(this.targetLocation.href, this.createCacheEntry(newPage))
          this.cache.get(this.targetLocation.href).view.createDom()
        })

        navigationPromise = this.beforeFetch(this.targetLocation, TransitionClass, trigger).then(async () => {
          return fetched.then(async (newPage) => {
            return await this.afterFetch(this.targetLocation, TransitionClass, this.cache.get(this.targetLocation.href), trigger)
          })
        })
      } else {
        this.cache.get(this.targetLocation.href).view.createDom()

        navigationPromise = this.beforeFetch(this.targetLocation, TransitionClass, trigger).then(async () => {
          return await this.afterFetch(this.targetLocation, TransitionClass, this.cache.get(this.targetLocation.href), trigger)
        })
      }

      navigationPromise.then(() => {
        resolve()
      })
    })
  }

  static on(event, callback) {
    Events.on(event, callback)
  }

  static off(event, callback) {
    Events.off(event, callback)
  }

  static beforeFetch(url, TransitionClass, trigger) {
    Events.emit(EventType.NAVIGATE_OUT, {
      from: this.currentCacheEntry,
      trigger,
    })

    return new Promise((resolve) => {
      this.currentCacheEntry.view.leave(TransitionClass, trigger, this.removeOldContent).then(() => {
        if (trigger !== 'popstate') {
          window.history.pushState({}, '', url.raw)
        }

        // Update nav active indicator as soon as the URL changes,
        // so the user sees the correct active page during the transition
        // rather than after it completes.
        Nav.updateNavActive()

        resolve()
      })
    })
  }

  static afterFetch(url, TransitionClass, entry, trigger) {
    this.currentLocation = url
    this.popTarget = this.currentLocation.href

    return new Promise((resolve) => {
      entry.view.update()

      Events.emit(EventType.NAVIGATE_IN, {
        from: this.currentCacheEntry,
        to: entry,
        trigger,
      })

      entry.view.enter(TransitionClass, trigger).then(() => {
        Events.emit(EventType.NAVIGATE_END, {
          from: this.currentCacheEntry,
          to: entry,
          trigger,
        })

        // Scroll to a hash target after navigating
        if (url.hasHash && trigger.dataset && trigger.dataset.scrollTo) {
          const hashTarget = document.querySelector(trigger.dataset.scrollTo)
          Layout.scroll.scrollTo(hashTarget, { immediate: true })
        }

        this.currentCacheEntry = entry
        this.isTransitioning = false
        this.isPopping = false
        resolve()
      })
    })
  }

  static attachEvents(links) {
    Events.delegate('click', links, this.onClick)
    Events.on('popstate', window, this.onPopstate)
  }

  static onClick = (e) => {
    if (this.active) {
      if (!(e.metaKey || e.ctrlKey)) {
        const currentView = document.querySelector('[data-view]')?.dataset?.view
        if (currentView === 'error') {
          return
        }

        const target = processUrl(e.currentTarget.href)
        this.currentLocation = processUrl(window.location.href)

        if (this.currentLocation.host !== target.host) {
          return
        }

        // the target is a new URL, or is removing the hash from the current URL
        if (this.currentLocation.href !== target.href || (this.currentLocation.hasHash && !target.hasHash)) {
          e.preventDefault()
          // noinspection JSIgnoredPromiseFromCall
          this.navigateTo(target.raw, e.currentTarget.dataset.transition || false, e.currentTarget).catch((err) => console.warn(err))
          return
        }

        // a click to the current URL was detected
        if (!this.currentLocation.hasHash && !target.hasHash) {
          e.preventDefault()
        }
      }
    }
  }

  static onPopstate = () => {
    // don't trigger for on-page anchors
    if (window.location.pathname === this.currentLocation.pathname && !this.isPopping) {
      return false
    }

    if (!this.allowInterruption && (this.isTransitioning || this.isPopping)) {
      // overwrite history state with current page if currently navigating
      window.history.pushState({}, '', this.popTarget)
      return false
    }

    if (!this.isPopping) {
      this.popTarget = window.location.href
    }

    this.isPopping = true

    const url = window.location.href
    if (/page\/\d+/.test(url)) {
      window.location.reload()
    } else {
      // noinspection JSIgnoredPromiseFromCall
      this.navigateTo(url, false, 'popstate')
    }
  }

  static async fetch(url, runFallback = true) {
    try {
      const response = await fetch(url, {
        mode: 'same-origin',
        method: 'GET',
        headers: { 'X-Requested-With': 'Performanse' },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        throw new Error(response.statusText)
      }

      const htmlString = await response.text()
      return parseDom(htmlString)
    } catch (err) {
      if (runFallback) {
        window.location.href = url
      }
      throw err
    }
  }

  static chooseTransition(transition) {
    if (transition) {
      return this.transitions[transition]
    }

    const routeTransition = this.router?.findMatch(this.currentLocation, this.targetLocation)

    if (routeTransition) {
      return this.transitions[routeTransition]
    }

    return this.defaultTransition
  }

  static createCacheEntry(page) {
    const content = page.querySelector('[data-view]')
    const View =
      content.dataset.view.length && this.views[content.dataset.view] !== undefined
        ? this.views[content.dataset.view]
        : this.views['default']

    if (!View) {
      console.warn(`"${content.dataset.view}" not found.`)
    }

    return {
      page,
      content,
      skipCache: content.hasAttribute('data-view-nocache'),
      title: page.title,
      view: new View({
        wrapper: this.wrapper,
        title: page.title,
        content,
        page,
      }),
    }
  }
}
